import { scoringConfigSchema, validateNoDuplicateKeys, validateNoLockedEdits, scoringErrorMessages } from "@/scoringUi";

/**
 * Every scoring-capable field in the job-offer form, wrapped as `{scoring}` (the shape
 * `sumMaxScore`/validation expect). Single source of truth for "what counts as a local
 * scoring criterion" -- keep in sync with the `localCriteria` list jobOfferMapper.js
 * actually sends on save.
 *
 * @param {object} values - formik values (the job offer)
 * @returns {Array<{scoring: object}>}
 */
export function getLocalScoringCriteria(values) {
    const tag = (step, section, labelOf) => (item) => ({ scoring: item.scoring, step, section, label: labelOf(item) });
    return [
        values.availability?.scoring && tag(5, "Dostępność", () => "")(values.availability),
        values.financialExpectations?.scoring && tag(5, "Oczekiwania finansowe", () => "")(values.financialExpectations),
        ...(values.employmentForms ?? []).map(tag(2, "Formy zatrudnienia", (x) => x.aiQuestion)),
        ...(values.motivationQuestions ?? []).map(tag(5, "Motywacja i dopasowanie", (x) => x.aiQuestion || x.questionText)),
        ...(values.requiredDocuments ?? []).map(tag(4, "Wymagane dokumenty", (x) => x.name)),
        ...(values.skills ?? []).map(tag(4, "Umiejętności", (x) => x.name)),
        ...(values.experienceRequirements ?? []).map(tag(4, "Doświadczenie", (x) => x.area)),
        ...(values.riskFactors ?? []).map(tag(3, "Czynniki ryzyka", (x) => x.name)),
        ...(values.customQuestions ?? []).map(tag(5, "Niestandardowe pytania", (x) => x.questionText)),
    ].filter((c) => c.scoring);
}

const RULE_LABELS = { valueRules: "reguła wartości", rangeRules: "reguła zakresu", setRules: "reguła zbioru", aiLevels: "poziom AI" };

function describeLocation({ step, section, label }) {
    const short = label && label.length > 60 ? `${label.slice(0, 57)}…` : label;
    return [`Krok ${step}`, section, short && `„${short}”`].filter(Boolean).join(" · ");
}

function describeError(inner, scoring) {
    const match = /^(\w+)\[(\d+)\]/.exec(inner.path ?? "");
    if (!match) return inner.message;
    const [, group, index] = match;
    const rule = scoring?.[group]?.[Number(index)];
    const detail = rule?.points != null && inner.message.includes("przekracza") ? ` (${rule.points} pkt, maks. kryterium: ${scoring.maxScore} pkt)` : "";
    return `${RULE_LABELS[group] ?? group} nr ${Number(index) + 1}: ${inner.message}${detail}`;
}

/**
 * Runs the blocking scoring validations from spec 2 section 7 across every
 * scoring-capable criterion currently in the job-offer form (see
 * getLocalScoringCriteria) and any scenario-criterion overrides. Returns
 * business-language messages (never raw field/enum names) -- empty array
 * means the form may be submitted.
 *
 * @param {object} values - formik values (the job offer)
 * @param {Array<{criterionKey: string, scoring?: object, overrideMode?: string}>} [modelCriteria] - the raw `criteria` array from GET .../scoring-model (EffectiveCriterion[], PascalCase overrideMode as the API sends it) for the LOCKED-edit check. Omitted when the recruitment has no scenario yet.
 * @returns {string[]}
 */
export function collectScoringValidationErrors(values, modelCriteria) {
    return collectScoringValidationIssues(values, modelCriteria).map((i) => i.text);
}

/** Same checks as collectScoringValidationErrors, but each issue carries the form `step` (when known) so the UI can jump to it. */
export function collectScoringValidationIssues(values, modelCriteria) {
    const issues = new Map();
    const add = (text, step = null) => issues.has(text) || issues.set(text, { text, step });

    for (const criterion of getLocalScoringCriteria(values)) {
        const { scoring } = criterion;
        try {
            scoringConfigSchema.validateSync(scoring, { context: { maxScore: scoring.maxScore }, abortEarly: false });
        } catch (err) {
            const inner = err.inner?.length ? err.inner : [{ message: err.message, path: "" }];
            for (const e of inner) add(`${describeLocation(criterion)}: ${describeError(e, scoring)}`, criterion.step);
        }
    }

    const overrideCriteria = (values.scoringOverrides ?? []).map((o) => ({ criterionKey: o.criterionKey, scoring: o.scoring }));
    const dupError = validateNoDuplicateKeys(overrideCriteria);
    if (dupError) add(dupError);

    if (modelCriteria?.length) {
        // The API sends overrideMode as raw PascalCase ("Locked"); this package's
        // validateNoLockedEdits expects its own UPPER_SNAKE_CASE constant -- same
        // boundary mismatch apiMapping.js handles elsewhere, normalized inline here
        // since this is the one place that needs the *criterion-level* field (not
        // the nested scoring object apiMapping.js already covers).
        const normalizedScenarioCriteria = modelCriteria.map((c) => ({
            criterionKey: c.criterionKey,
            scoring: c.scoring,
            overrideMode: c.overrideMode === "Locked" ? "LOCKED" : c.overrideMode === "AllowRecruitmentOverride" ? "ALLOW_RECRUITMENT_OVERRIDE" : null,
        }));
        const lockedError = validateNoLockedEdits(overrideCriteria, normalizedScenarioCriteria);
        if (lockedError) add(lockedError);
    }

    return Array.from(issues.values());
}

export { scoringErrorMessages };
